import {
  annotationSizeMm,
  dimensionGeometry,
  dimensionSizeMm,
  DIM_COLOR,
  DIM_DASH_MM,
  DIM_TICK_MM,
  DIM_WIDTH_MM,
  ANNOT_COLOR,
  polylinePathData,
  type Annotation2D,
  type Dimension2D,
  type Point2,
  type Polyline2D,
  type SnapPoint,
} from './lib'
import { measureSummary, type MeasureResult } from './measure'

// Glifos desenhados sobre a pré-visualização: etiquetas de anotação,
// medição em curso e marcas dos cortes gravados. São todos "anotação de
// ecrã": as dimensões vêm em unidades do SVG por pixel (`unit`) para não
// encolherem com o zoom.

/** Cor das medições em curso (mesma família do traço de desenhar
 *  cortes: é sempre "trabalho por cima do desenho"). */
export const MEASURE_COLOR = '#0284c7'
/** Realce da anotação seleccionada. */
export const ANNOT_SELECT_COLOR = '#0d9488'
/** Marca do snap. Magenta da paleta bSPT: não colide com o azul da
 *  medição, o âmbar do traço de corte nem o vermelho das marcas. */
export const SNAP_COLOR = '#9d358a'
/** Altura mínima do texto das anotações na PRÉ-VISUALIZAÇÃO, em pixéis
 *  de ecrã. As anotações desenham-se ao tamanho real de papel (2,5 mm),
 *  mas com o desenho enquadrado isso pode dar meia dúzia de centésimas
 *  de pixel; o mínimo mantém-nas legíveis, tal como já se faz com a
 *  espessura dos traços. Não afecta os exports. */
export const ANNOT_MIN_PX = 9

export interface SvgMetricsLike {
  toSvg: (x: number, y: number) => [number, number]
}

/** Etiqueta de texto sobre o desenho: âncora na linha de base, como nos
 *  writers. Em modo "Anotar" é agarrável (arrasto), editável (duplo
 *  clique) e traz um × quando está seleccionada. Fora desse modo é só
 *  desenho, e os cliques passam ao SVG de baixo (selecção de
 *  elementos). */
export function AnnotGlyph({
  annot,
  metrics,
  unit,
  selected,
  hidden,
  interactive,
  onDown,
  onEdit,
  onDelete,
}: {
  annot: Annotation2D
  metrics: SvgMetricsLike
  unit: number
  selected: boolean
  /** A etiqueta está a ser editada na caixa de texto: esconde-se para
   *  não se ver o texto velho por baixo. */
  hidden: boolean
  interactive: boolean
  onDown: (e: React.PointerEvent<SVGGElement>, a: Annotation2D) => void
  onEdit: (a: Annotation2D) => void
  onDelete: (id: string) => void
}) {
  if (hidden) return null
  const [x, y] = metrics.toSvg(annot.x, annot.y)
  // Tamanho real de papel, com um mínimo em pixéis de ecrã para a
  // etiqueta não desaparecer com o desenho enquadrado.
  const size = Math.max(annotationSizeMm(annot), unit * ANNOT_MIN_PX)
  const color = selected ? ANNOT_SELECT_COLOR : ANNOT_COLOR
  const width = size * 0.55 * Math.max(annot.text.length, 1)
  return (
    <g
      style={{ pointerEvents: interactive ? 'auto' : 'none' }}
      onPointerDown={interactive ? (e) => onDown(e, annot) : undefined}
      onDoubleClick={
        interactive
          ? (e) => {
              e.stopPropagation()
              onEdit(annot)
            }
          : undefined
      }
    >
      {/* Alvo de rato generoso e invisível: apanhar texto fino de 2,5 mm
          com o rato era um exercício de pontaria. */}
      <rect
        x={x - size * 0.3}
        y={y - size}
        width={width + size * 0.6}
        height={size * 1.4}
        fill={selected ? ANNOT_SELECT_COLOR : 'transparent'}
        fillOpacity={selected ? 0.12 : 0}
        stroke={selected ? ANNOT_SELECT_COLOR : 'none'}
        strokeWidth={selected ? unit : 0}
        style={{ cursor: interactive ? 'move' : 'default' }}
      />
      <text
        x={x}
        y={y}
        fontSize={size}
        fill={color}
        stroke="none"
        fontFamily="Helvetica, Arial, sans-serif"
        style={{ cursor: interactive ? 'move' : 'default' }}
      >
        {annot.text}
      </text>
      {selected && interactive && (
        <g
          onPointerDown={(e) => {
            e.stopPropagation()
            onDelete(annot.id)
          }}
          style={{ cursor: 'pointer' }}
        >
          <circle
            cx={x + width + size * 0.9}
            cy={y - size * 0.35}
            r={size * 0.6}
            fill="#b91c1c"
          />
          <text
            x={x + width + size * 0.9}
            y={y - size * 0.05}
            fontSize={size * 0.9}
            fill="#ffffff"
            stroke="none"
            textAnchor="middle"
            fontFamily="Helvetica, Arial, sans-serif"
          >
            ×
          </text>
        </g>
      )}
    </g>
  )
}

/** Medição em curso ou fechada: vértices, troços, linha elástica e o
 *  valor no centróide. Tudo em unidades de ecrã (é anotação de trabalho,
 *  não entra nos ficheiros). */
export function MeasureGlyph({
  points,
  cursor,
  closed,
  result,
  metrics,
  unit,
}: {
  points: Point2[]
  cursor: Point2 | null
  closed: boolean
  result: MeasureResult | null
  metrics: SvgMetricsLike
  unit: number
}) {
  if (points.length === 0) return null
  const svgPts = points.map((p) => metrics.toSvg(p[0], p[1]))
  const d =
    svgPts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p[0]} ${p[1]}`).join(' ') +
    (closed && svgPts.length >= 3 ? ' Z' : '')
  const last = svgPts[svgPts.length - 1]
  const cur = cursor ? metrics.toSvg(cursor[0], cursor[1]) : null
  const label = result ? measureSummary(result) : ''
  const centre = result
    ? metrics.toSvg(result.centroid[0], result.centroid[1])
    : null
  return (
    <g>
      {closed && svgPts.length >= 3 && (
        <path d={d} fill={MEASURE_COLOR} fillOpacity={0.12} stroke="none" />
      )}
      <path
        d={d}
        fill="none"
        stroke={MEASURE_COLOR}
        strokeWidth={unit * 1.6}
        strokeLinejoin="round"
      />
      {cur && (
        <line
          x1={last[0]}
          y1={last[1]}
          x2={cur[0]}
          y2={cur[1]}
          stroke={MEASURE_COLOR}
          strokeWidth={unit * 1.2}
          strokeDasharray={`${unit * 5} ${unit * 3}`}
        />
      )}
      {svgPts.map((p, i) => (
        <circle
          key={i}
          cx={p[0]}
          cy={p[1]}
          r={unit * 2.5}
          fill="#ffffff"
          stroke={MEASURE_COLOR}
          strokeWidth={unit * 1.2}
        />
      ))}
      {closed && centre && label && (
        <text
          x={centre[0]}
          y={centre[1]}
          fontSize={unit * 11}
          fill={MEASURE_COLOR}
          stroke="#ffffff"
          strokeWidth={unit * 2.5}
          paintOrder="stroke"
          textAnchor="middle"
          fontFamily="Helvetica, Arial, sans-serif"
        >
          {label}
        </text>
      )}
    </g>
  )
}

/** Cota já terminada: linha(s) de cota, traços de extensão nas pontas e
 *  o valor rodado sobre a linha. É a MESMA geometria que vai para o
 *  SVG, o PDF e o DXF (`dimensionGeometry`), desenhada com as mesmas
 *  grandezas de papel: o que se vê é o que sai nos ficheiros.
 *
 *  A única liberdade é o mínimo em pixéis de ecrã, igual ao das
 *  anotações: com o desenho enquadrado, 2,5 mm de papel podem valer
 *  meia dúzia de centésimas de pixel. Tudo (texto, traços, afastamentos)
 *  cresce pelo MESMO factor, senão a composição desfazia-se. */
export function DimGlyph({
  dim,
  metrics,
  unit,
  selected,
  interactive,
  onSelect,
  onDelete,
}: {
  dim: Dimension2D
  metrics: SvgMetricsLike
  unit: number
  selected: boolean
  interactive: boolean
  onSelect: (e: React.PointerEvent<SVGGElement>, id: string) => void
  onDelete: (id: string) => void
}) {
  const geo = dimensionGeometry(dim)
  const baseSize = dimensionSizeMm(dim)
  const size = Math.max(baseSize, unit * ANNOT_MIN_PX)
  const f = size / baseSize
  const tick = DIM_TICK_MM * f
  const width = Math.max(DIM_WIDTH_MM * f, unit)
  const color = selected ? ANNOT_SELECT_COLOR : DIM_COLOR
  const to = (p: Point2): [number, number] => metrics.toSvg(p[0], p[1])
  // Alvo de rato: os mesmos traços com uma espessura generosa e
  // invisível (apanhar um traço de 0,18 mm à mão era pontaria).
  const hit = [...geo.lines, ...geo.dashed]
    .map((ln) => {
      const a = to(ln.a)
      const b = to(ln.b)
      return `M ${a[0]} ${a[1]} L ${b[0]} ${b[1]}`
    })
    .join(' ')
  const lastPt = dim.points[dim.points.length - 1]
  const [lx, ly] = to(lastPt)
  return (
    <g
      style={{ pointerEvents: interactive ? 'auto' : 'none' }}
      onPointerDown={interactive ? (e) => onSelect(e, dim.id) : undefined}
      data-testid="views2d-dim"
    >
      {interactive && hit && (
        <path
          d={hit}
          fill="none"
          stroke="transparent"
          strokeWidth={unit * 8}
          style={{ cursor: 'pointer' }}
        />
      )}
      {geo.lines.map((ln, i) => {
        const a = to(ln.a)
        const b = to(ln.b)
        return (
          <line
            key={`l${i}`}
            x1={a[0]}
            y1={a[1]}
            x2={b[0]}
            y2={b[1]}
            stroke={color}
            strokeWidth={width}
          />
        )
      })}
      {geo.dashed.map((ln, i) => {
        const a = to(ln.a)
        const b = to(ln.b)
        return (
          <line
            key={`d${i}`}
            x1={a[0]}
            y1={a[1]}
            x2={b[0]}
            y2={b[1]}
            stroke={color}
            strokeWidth={width}
            strokeDasharray={`${DIM_DASH_MM[0] * f} ${DIM_DASH_MM[1] * f}`}
          />
        )
      })}
      {geo.ticks.map((t, i) => {
        const [cx, cy] = to(t.at)
        return (
          <line
            key={`t${i}`}
            x1={cx - t.nx * tick}
            y1={cy + t.ny * tick}
            x2={cx + t.nx * tick}
            y2={cy - t.ny * tick}
            stroke={color}
            strokeWidth={width}
          />
        )
      })}
      {geo.texts.map((t, i) => {
        const [ax, ay] = to(t.at)
        const tx = ax + t.nx * t.offsetMm * f
        const ty = ay - t.ny * t.offsetMm * f
        const deg = (-t.angleRad * 180) / Math.PI
        return (
          <text
            key={`x${i}`}
            x={tx}
            y={ty}
            fontSize={size}
            fill={color}
            stroke="#ffffff"
            strokeWidth={size * 0.16}
            paintOrder="stroke"
            textAnchor="middle"
            fontFamily="Helvetica, Arial, sans-serif"
            transform={deg ? `rotate(${deg} ${tx} ${ty})` : undefined}
          >
            {t.text}
          </text>
        )
      })}
      {selected && interactive && (
        <g
          onPointerDown={(e) => {
            e.stopPropagation()
            onDelete(dim.id)
          }}
          style={{ cursor: 'pointer' }}
          data-testid="views2d-dim-delete"
        >
          <circle
            cx={lx + size * 1.2}
            cy={ly - size * 1.2}
            r={size * 0.6}
            fill="#b91c1c"
          />
          <text
            x={lx + size * 1.2}
            y={ly - size * 0.9}
            fontSize={size * 0.9}
            fill="#ffffff"
            stroke="none"
            textAnchor="middle"
            fontFamily="Helvetica, Arial, sans-serif"
          >
            ×
          </text>
        </g>
      )}
    </g>
  )
}

/** Marca do ponto agarrado pelo snap. A forma diz O QUE se agarrou, que
 *  é a convenção de qualquer CAD e evita ter de ler texto no meio de um
 *  gesto: quadrado = vértice, triângulo = ponto médio, círculo = centro.
 *  Traço fino e em unidades de ecrã: é ajuda ao desenho, não desenho, e
 *  nunca entra nos ficheiros. */
export function SnapGlyph({
  hit,
  metrics,
  unit,
}: {
  hit: SnapPoint | null
  metrics: SvgMetricsLike
  unit: number
}) {
  if (!hit) return null
  const [x, y] = metrics.toSvg(hit.p[0], hit.p[1])
  const r = unit * 5
  const common = {
    fill: 'none',
    stroke: SNAP_COLOR,
    strokeWidth: unit * 1.3,
    strokeLinejoin: 'round' as const,
  }
  return (
    <g style={{ pointerEvents: 'none' }} data-testid="views2d-snap-marker" data-snap-kind={hit.kind}>
      {hit.kind === 'vertex' && (
        <rect x={x - r} y={y - r} width={r * 2} height={r * 2} {...common} />
      )}
      {hit.kind === 'midpoint' && (
        <polygon
          points={`${x},${y - r} ${x + r},${y + r * 0.7} ${x - r},${y + r * 0.7}`}
          {...common}
        />
      )}
      {/* Intersecção: o X pequeno é a convenção do CAD, e não se
          confunde com o quadrado do vértice nem com o triângulo do
          ponto médio. */}
      {hit.kind === 'intersection' && (
        <>
          <line
            x1={x - r * 0.8}
            y1={y - r * 0.8}
            x2={x + r * 0.8}
            y2={y + r * 0.8}
            {...common}
          />
          <line
            x1={x - r * 0.8}
            y1={y + r * 0.8}
            x2={x + r * 0.8}
            y2={y - r * 0.8}
            {...common}
          />
        </>
      )}
      {hit.kind === 'center' && <circle cx={x} cy={y} r={r} {...common} />}
    </g>
  )
}

/** Marca de corte sobre a planta: traço, setas do lado para onde se
 *  olha e etiqueta nas duas pontas (convenção do desenho técnico). */
export function MarkGlyph({
  mark,
  metrics,
  unit,
}: {
  mark: {
    label: string
    a: [number, number]
    b: [number, number]
    dir: [number, number]
  }
  metrics: SvgMetricsLike
  /** Unidades do SVG por pixel de ecrã, para a marca não encolher com o
   *  zoom (o traço e a etiqueta são anotação de ecrã, não desenho). */
  unit: number
}) {
  const [ax, ay] = metrics.toSvg(mark.a[0], mark.a[1])
  const [bx, by] = metrics.toSvg(mark.b[0], mark.b[1])
  // O Y do SVG cresce para baixo: a componente vertical da direcção
  // troca de sinal ao passar para o papel.
  const dl = Math.hypot(mark.dir[0], mark.dir[1]) || 1
  const arrow = unit * 14
  const dx = (mark.dir[0] / dl) * arrow
  const dy = (-mark.dir[1] / dl) * arrow
  return (
    <g stroke="#b91c1c" fill="#b91c1c" strokeWidth={unit * 1.4}>
      <line
        x1={ax}
        y1={ay}
        x2={bx}
        y2={by}
        strokeDasharray={`${unit * 8} ${unit * 3} ${unit * 1.5} ${unit * 3}`}
      />
      <line x1={ax} y1={ay} x2={ax + dx} y2={ay + dy} />
      <line x1={bx} y1={by} x2={bx + dx} y2={by + dy} />
      <text
        x={ax - dx * 0.6}
        y={ay - dy * 0.6}
        fontSize={unit * 11}
        stroke="none"
        textAnchor="middle"
      >
        {mark.label}
      </text>
      <text
        x={bx - dx * 0.6}
        y={by - dy * 0.6}
        fontSize={unit * 11}
        stroke="none"
        textAnchor="middle"
      >
        {mark.label}
      </text>
    </g>
  )
}

// --- Escolher geometria já desenhada ---------------------------------------

/** Realce do que está ESCOLHIDO nos modos "Escolher linhas" e "Área do
 *  elemento". Mesma família do azul da medição (é sempre trabalho por
 *  cima do desenho), mas mais escuro para se distinguir da linha
 *  elástica. */
export const PICK_COLOR = '#0369a1'
/** Realce do que está debaixo do cursor e ainda não foi escolhido. */
export const PICK_HOVER_COLOR = '#38bdf8'

/** Métricas com o factor de escala: os arcos precisam do raio em
 *  unidades do SVG, não só do mapeamento dos pontos. */
export interface PathMetricsLike extends SvgMetricsLike {
  /** Unidades do SVG (mm de papel) por metro do desenho. */
  k: number
}

/** Caminho SVG de uma polilinha nas coordenadas do OVERLAY. Usa o mesmo
 *  `polylinePathData` do writer: um segundo tradutor de arcos era a
 *  maneira certa de o realce um dia deixar de coincidir com o desenho.
 *
 *  A transformação da pré-visualização é separável (o x do SVG só
 *  depende do x do desenho e o y só do y, ver `drawingSvgMap`), por isso
 *  dá para servir ao writer as duas funções de uma coordenada só que ele
 *  espera. */
function overlayPathData(pl: Polyline2D, metrics: PathMetricsLike): string {
  return polylinePathData(pl, {
    fx: (x) => String(metrics.toSvg(x, 0)[0]),
    fy: (y) => String(metrics.toSvg(0, y)[1]),
    k: metrics.k,
    decimals: 4,
  })
}

/** Caminho de um anel de pontos (contorno de uma região já medida). */
function ringPath(pts: Point2[], metrics: SvgMetricsLike): string {
  if (pts.length < 2) return ''
  return (
    pts
      .map((p, i) => {
        const [x, y] = metrics.toSvg(p[0], p[1])
        return `${i === 0 ? 'M' : 'L'} ${x} ${y}`
      })
      .join(' ') + ' Z'
  )
}

/** O que está escolhido (e o que está debaixo do cursor) nos modos de
 *  escolha. A região só aparece quando a fronteira fecha: enquanto está
 *  aberta, o que interessa é ver os troços e o comprimento. */
export function PickGlyph({
  picked,
  hover,
  region,
  label,
  labelAt,
  metrics,
  unit,
}: {
  picked: Polyline2D[]
  hover: Polyline2D | null
  /** Contorno fechado a preencher: exterior mais os furos (regra
   *  par-ímpar, como o poché). */
  region: { outline: Point2[]; rings: Point2[][] } | null
  label: string
  labelAt: Point2 | null
  metrics: PathMetricsLike
  unit: number
}) {
  const fill =
    region && region.outline.length >= 3
      ? [region.outline, ...region.rings].map((r) => ringPath(r, metrics)).join(' ')
      : ''
  const [lx, ly] = labelAt ? metrics.toSvg(labelAt[0], labelAt[1]) : [0, 0]
  return (
    <g style={{ pointerEvents: 'none' }} data-testid="views2d-pick-glyph">
      {fill && (
        <path
          d={fill}
          fill={PICK_COLOR}
          fillOpacity={0.14}
          fillRule="evenodd"
          stroke="none"
        />
      )}
      {hover && (
        <path
          d={overlayPathData(hover, metrics)}
          fill="none"
          stroke={PICK_HOVER_COLOR}
          strokeWidth={unit * 3.5}
          strokeOpacity={0.75}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      )}
      {picked.map((pl, i) => (
        <path
          key={i}
          d={overlayPathData(pl, metrics)}
          fill="none"
          stroke={PICK_COLOR}
          strokeWidth={unit * 3}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ))}
      {label && labelAt && (
        <text
          x={lx}
          y={ly}
          fontSize={unit * 11}
          fill={PICK_COLOR}
          stroke="#ffffff"
          strokeWidth={unit * 2.5}
          paintOrder="stroke"
          textAnchor="middle"
          fontFamily="Helvetica, Arial, sans-serif"
        >
          {label}
        </text>
      )}
    </g>
  )
}
